/**
 * usePackPurchase — compra de pacote de créditos, compartilhada entre Integrações e /billing.
 *
 * Comprar abre SEMPRE a modal "Dados para o pagamento" (CPF ou CNPJ + nome ou razão social + MEIO).
 * Planos: docs/PLANO-DOCUMENTO-CPF-CNPJ-E-COBRANCA.md (dados) e docs/PLANO-CARTAO-PACOTES.md (meio).
 *
 * 🔑 Genérico por meio de pagamento, nunca por gateway: o que fazer depois do pedido sai da FORMA da
 * resposta da API — `alreadyPaid` → aviso; `pix` → QR na tela; `checkoutUrl` → ir à página de
 * pagamento do gateway (cartão). Trocar o gateway do cartão (Asaas → Mercado Pago) é configuração do
 * Billing e não muda nada aqui.
 *
 * Uso:
 *   const compra = usePackPurchase({ onPaid, showError, showSuccess });
 *   compra.handleBuy(produto)              // comprar: abre a modal de dados
 *   compra.handleResume(produto, meio)     // retomar a compra pendente: SEM modal
 *   <DadosDeCobrancaDialog {...compra.cobranca} />
 *   {compra.checkoutOrder && <PixQrCheckout order={compra.checkoutOrder} onClose={compra.closeCheckout} />}
 */
import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import billingService from '../services/billingService';
import useDadosDeCobranca from './useDadosDeCobranca';

const obterMetodosDoPacote = () => billingService.getPaymentMethods('pack');

export default function usePackPurchase({ onPaid, showError, showSuccess } = {}) {
  const queryClient = useQueryClient();
  const [checkoutOrder, setCheckoutOrder] = useState(null);
  const cobranca = useDadosDeCobranca();

  // A API é quem garante "uma compra por pacote": abrir de novo devolve o MESMO PIX (reused) e, se o
  // pagamento já entrou — inclusive por outra máquina —, responde alreadyPaid em vez de um QR novo.
  // A tela nunca decide isso sozinha (17/09/2026: dois PIX do mesmo pacote, um pago e outro pagável).
  // Com cartão, a resposta traz `checkoutUrl`: a pessoa vai à página do gateway e volta por
  // /billing/return, que confirma e libera os créditos.
  const seguirPedido = useCallback((order) => {
    if (order?.alreadyPaid) {
      queryClient.invalidateQueries({ queryKey: ['entitlements'] });
      onPaid?.();
      showSuccess?.('Este pacote já foi pago. Os créditos estão na sua conta.');
      return;
    }
    if (order?.pix) { setCheckoutOrder(order); return; }
    if (order?.checkoutUrl) { window.location.assign(order.checkoutUrl); return; }
    showError?.('Não foi possível iniciar o pagamento. Tente novamente.');
  }, [queryClient, onPaid, showSuccess, showError]);

  // Sem providerSlug: escolher adquirente é do Billing (ele vê taxa de sucesso e quem caiu).
  // Mandar o slug daqui vazava o adquirente para o front e impedia o Billing de desviar.
  const comprarComDados = useCallback(async (product, { document, billingName, method }) => {
    try {
      const order = await billingService.createOrder({
        kind: 'pack', productId: product.id, methods: [method || 'Pix'], document, billingName,
      });
      seguirPedido(order);
      return { ok: true };
    } catch (err) {
      return {
        ok: false,
        erro: err?.response?.data?.message || 'Não foi possível iniciar o pagamento. Tente novamente.',
      };
    }
  }, [seguirPedido]);

  const { abrir } = cobranca;
  const handleBuy = useCallback((product) => {
    if (!product) { showError?.('Não foi possível iniciar o pagamento. Tente novamente.'); return; }
    abrir((dados) => comprarComDados(product, dados), { obterMetodos: obterMetodosDoPacote });
  }, [abrir, comprarComDados, showError]);

  // RETOMAR a compra pendente NÃO abre a modal: no PIX a API devolve o MESMO PIX; no cartão cria um
  // pedido novo com os dados salvos e leva de volta à página de pagamento. Só se a API recusar por
  // falta de documento (nada salvo) é que a modal abre.
  const handleResume = useCallback(async (product, method = 'Pix') => {
    if (!product) { showError?.('Não foi possível iniciar o pagamento. Tente novamente.'); return; }
    try {
      seguirPedido(await billingService.createOrder({ kind: 'pack', productId: product.id, methods: [method] }));
    } catch (err) {
      if (err?.response?.status === 400) abrir((dados) => comprarComDados(product, dados), { obterMetodos: obterMetodosDoPacote });
      else showError?.(err?.response?.data?.message || 'Não foi possível retomar o pagamento. Tente novamente.');
    }
  }, [abrir, comprarComDados, seguirPedido, showError]);

  // Ao fechar o checkout, quem chamou recarrega o que precisa (o pagamento pode ter concedido lote novo).
  const closeCheckout = useCallback(() => { setCheckoutOrder(null); onPaid?.(); }, [onPaid]);

  return {
    checkoutOrder,
    closeCheckout,
    handleBuy,
    handleResume,
    cobranca: { vm: cobranca.vm, actions: cobranca.actions },
  };
}
